/**
 * Public waitlist landing page (develio.com/waitlist).
 *
 * A low-friction capture page for EDC directors who are interested but not
 * ready to start the card-required trial. Collects an email and posts it to
 * the public POST /api/v1/waitlist endpoint.
 */

import { type FormEvent, useState } from 'react';

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';
const WAITLIST_MODE = import.meta.env.VITE_WAITLIST_MODE === 'true';

const VALUE_POINTS = [
  'Jobs, tax revenue, and ROI for any project, in minutes',
  'Grounded in federal BEA RIMS II data, defensible to your board',
  'Branded PDF reports with plain-English summaries',
];

export function WaitlistRoute() {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState(''); // honeypot
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setError(null);
    try {
      const res = await fetch(`${API}/api/v1/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          company, // honeypot; real users leave this empty
          source: 'waitlist_page',
          referrer: typeof document !== 'undefined' ? document.referrer : undefined,
        }),
      });
      if (res.ok) {
        setStatus('done');
      } else {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setError(body?.error ?? 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch {
      setError('Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-brand-teal-50 via-white to-brand-teal-50/40 px-4 py-12">
      <div className="w-full max-w-xl">
        <div className="mb-8 text-center">
          <img src="/logo.png" alt="Develio" className="mx-auto mb-8 h-12 w-auto object-contain" />
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-teal-700">
            Fiscal &amp; economic impact for economic developers
          </p>
          <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-slate-900 sm:text-[2.75rem] sm:leading-[1.05]">
            Make the case for <span className="text-develio-green">every deal.</span>
          </h1>
          <p className="mx-auto mt-4 max-w-md text-base text-slate-500">
            Develio turns any development project into a board-ready fiscal impact report. Join the
            list to learn more and get updates.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          {status === 'done' ? (
            <div className="py-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-develio-green/10 text-2xl text-develio-green">
                ✓
              </div>
              <h2 className="text-lg font-semibold text-slate-900">You&rsquo;re on the list.</h2>
              <p className="mt-1 text-sm text-slate-500">
                Thanks for your interest in Develio. We&rsquo;ll be in touch soon.
              </p>
            </div>
          ) : (
            <>
              <ul className="mb-6 space-y-2">
                {VALUE_POINTS.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="mt-0.5 shrink-0 text-develio-green">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <form onSubmit={submit} className="space-y-3">
                {/* Honeypot: hidden from users, catches bots. */}
                <input
                  type="text"
                  name="company"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="absolute left-[-9999px] h-0 w-0 opacity-0"
                />
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourcity.gov"
                    className="w-full flex-1 rounded-lg border border-slate-300 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-brand-teal-500 focus:ring-2 focus:ring-brand-teal-500/30"
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="shrink-0 rounded-lg bg-brand-teal-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-brand-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {status === 'loading' ? 'Joining…' : 'Join the list'}
                  </button>
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <p className="text-center text-xs text-slate-400">
                  No spam. We&rsquo;ll only email you about Develio.
                </p>
              </form>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          {!WAITLIST_MODE && (
            <>
              Already know you want in?{' '}
              <a href="/sign-up" className="font-medium text-brand-teal-700 underline">
                Start your free trial
              </a>
              . &nbsp;·&nbsp;{' '}
            </>
          )}
          A Melio Consulting Group product.
        </p>
      </div>
    </div>
  );
}
